import { create } from 'zustand'
import { combine, devtools, persist } from 'zustand/middleware'

/**
 * Which notes the participant list shows: all, only the current user's, or only
 * assessment-kind ones (the Assessments tab).
 */
export type ParticipantNotesView = 'all' | 'mine' | 'assessments'

// Keyed by the union so a new view can't be added without being handled here.
const NOTES_VIEWS: Record<ParticipantNotesView, true> = {
  all: true,
  mine: true,
  assessments: true,
}

function isNotesView(value: unknown): value is ParticipantNotesView {
  return typeof value === 'string' && value in NOTES_VIEWS
}

// Browser-level (localStorage) memory of the last notes view the user landed on
// — All, My, or Assessments. The chart Notes nav reopens this view for every
// participant. It's a per-browser UI preference, deliberately not stored on the
// Practitioner. Mirrors the persist pattern in useInboxCategoryStore; default
// 'all' when unset.
export const useNotesViewStore = create(
  devtools(
    persist(
      combine({ lastNotesView: 'all' as ParticipantNotesView }, (set) => ({
        setLastNotesView: (view: ParticipantNotesView) => {
          set({ lastNotesView: view })
        },
      })),
      {
        name: 'notes-view-store',
        version: 1,
        // Coerce any stale or hand-edited localStorage value back into the
        // union; anything outside it falls back to the 'all' default.
        migrate: (persisted) => {
          const state = persisted as { lastNotesView?: string }
          const lastNotesView: ParticipantNotesView = isNotesView(
            state.lastNotesView
          )
            ? state.lastNotesView
            : 'all'
          return { lastNotesView }
        },
      }
    )
  )
)
